import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  EMPTY,
  Observable,
  catchError,
  map,
  of,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';

import { environment } from '../../environments/environment';
import { JsonResponse } from '../shared/api/backend-config';
import { hasStoredAccessToken } from '../user/auth-tokens';
import type { RequestedReport } from './reports.service';

/**
 * What a guest's browser knows about the order it just paid for.
 * - `confirming`: Stripe has not told the backend yet, so the order is not paid.
 * - `order`: its reports, built or still building.
 * - `gone`: no longer readable by id - the window passed, or it never was paid.
 */
export type GuestOrderView =
  | { kind: 'confirming' }
  | { kind: 'order'; reports: RequestedReport[] }
  | { kind: 'gone' };

const STORAGE_KEY = 'guest_paid_order';

interface StoredOrder {
  requestId: string;
  at: number;
}

// The backend reads an order for 2 hours after payment, and a Checkout
// Session can be paid up to 24 hours after it was opened.
const STORED_MAX_MS = 26 * 60 * 60 * 1000;

const POLL_MS = 3000;

// How long a paid return may wait for the webhook before we stop asking.
const CONFIRMING_MAX_MS = 10 * 60 * 1000;

// ReportStatus values, spelled out: the enum lives in the My reports
// component, which imports this service.
const BUILDING = new Set<string>(['ORDERED', 'IN_PROGRESS_AUTOMATED']);

/**
 * Lets a guest see the report they just paid for, with no account session.
 *
 * The order's request id is the access check: a random UUID that only this
 * browser was given by `POST /report/order`. So it is kept in localStorage
 * across the Stripe round-trip and sent in a request body - never in a URL,
 * where history, referrers and analytics would keep a copy of it.
 */
@Injectable({ providedIn: 'root' })
export class GuestOrderService {
  constructor(private http: HttpClient) {}

  /** Signed-in buyers see their orders in My reports, they need no id kept. */
  public rememberCheckout(requestId: string): void {
    if (hasStoredAccessToken()) return;
    const stored: StoredOrder = { requestId, at: Date.now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // private mode or full storage: the guest still gets the report by email
    }
  }

  public storedRequestId(): string | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const stored = JSON.parse(raw) as StoredOrder;
      if (!stored.requestId || Date.now() - stored.at > STORED_MAX_MS) {
        this.forget();
        return null;
      }
      return stored.requestId;
    } catch {
      return null;
    }
  }

  public forget(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // nothing kept, nothing to forget
    }
  }

  /**
   * Polls the order until every report has settled, or until it cannot be
   * read. Cold, and completes on its own.
   *
   * @param justPaid the browser is back from Stripe's success page. Only then
   *   is an unreadable order one whose webhook is still on its way; on any
   *   other visit it is a checkout that was never paid, and is dropped.
   */
  public watch(
    requestId: string,
    justPaid: boolean
  ): Observable<GuestOrderView> {
    const startedAt = Date.now();
    let seen = false;

    return timer(0, POLL_MS).pipe(
      switchMap(() => this.read(requestId)),
      map((view): GuestOrderView => {
        if (view.kind === 'order') {
          seen = true;
          return view;
        }
        // Read before, unreadable now: the window has closed.
        if (!justPaid || seen || Date.now() - startedAt > CONFIRMING_MAX_MS) {
          return { kind: 'gone' };
        }
        return view;
      }),
      takeWhile((view) => {
        if (view.kind === 'gone') {
          this.forget();
          return false;
        }
        return (
          view.kind === 'confirming' ||
          view.reports.some((r) => BUILDING.has(r.status))
        );
      }, true)
    );
  }

  private read(requestId: string): Observable<GuestOrderView> {
    return this.http
      .post<
        JsonResponse<RequestedReport[]>
      >(`${environment.apiUrl}report/order/status`, { requestId })
      .pipe(
        map(
          (res): GuestOrderView => ({
            kind: 'order',
            reports: res.object ?? [],
          })
        ),
        // A 404 is an answer. Anything else is a dropped poll: skip it, the
        // next tick asks again.
        catchError((err: unknown) =>
          err instanceof HttpErrorResponse && err.status === 404
            ? of<GuestOrderView>({ kind: 'confirming' })
            : EMPTY
        )
      );
  }
}
